import React, { useState, useEffect } from 'react';
import { getAvails, AvailsFilters } from '../services/availsApi';
import { Show, AvailComic } from '../types/Avail';
import { createBooking, getBookings } from '../services/bookingsApi';
import { Booking } from '../types/Booking';
import './AvailsList.css';

const AvailsList: React.FC = () => {
  const [shows, setShows] = useState<Show[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingInProgress, setBookingInProgress] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [selectedShow, setSelectedShow] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [hostFilter, setHostFilter] = useState<string>('all');
  const [friendFilter, setFriendFilter] = useState<string>('all');
  const [cellarFilter, setCellarFilter] = useState<string>('all');

  useEffect(() => {
    loadAvails();
  }, []);

  const loadAvails = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: AvailsFilters = {};

      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (genderFilter !== 'all') filters.gender = genderFilter;
      if (hostFilter === 'yes') filters.can_host = true;
      if (hostFilter === 'no') filters.can_host = false;
      if (friendFilter === 'yes') filters.is_friend = true;
      if (friendFilter === 'no') filters.is_friend = false;
      if (cellarFilter === 'yes') filters.is_cellar_comic = true;
      if (cellarFilter === 'no') filters.is_cellar_comic = false;

      const data = await getAvails(filters);
      setShows(data.shows);
    } catch (err: any) {
      console.error('Error loading avails:', err);
      setError(err.message || 'Failed to load avails');
    } finally {
      setLoading(false);
    }
  };

  const loadBookingsForShow = async (showDateTime: string) => {
    try {
      // Load bookings for the specific show date
      console.log('Loading bookings for show:', showDateTime);
      const bookingsData = await getBookings({ show_date_time: showDateTime });
      console.log('Bookings API response:', bookingsData);
      const showBookings: Booking[] = [];
      bookingsData.shows?.forEach(show => {
        console.log('Processing show:', show.show_date_time, 'bookings:', show.bookings);
        showBookings.push(...show.bookings);
      });
      console.log('Total bookings loaded:', showBookings.length);
      setBookings(showBookings);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
    }
  };

  const handleApplyFilters = () => {
    loadAvails();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setGenderFilter('all');
    setHostFilter('all');
    setFriendFilter('all');
    setCellarFilter('all');
  };

  const getTotalComics = () => {
    return shows.reduce((sum, show) => sum + show.total_comics, 0);
  };

  const getTotalHosts = () => {
    return shows.reduce((sum, show) => sum + show.hosts_count, 0);
  };

  const getFilteredComics = (show: Show) => {
    if (!searchTerm) return show.comics;

    const term = searchTerm.toLowerCase();
    return show.comics.filter(comic =>
      comic.comic_name.toLowerCase().includes(term) ||
      comic.comic_email.toLowerCase().includes(term)
    );
  };

  const getSelectedShowComics = () => {
    if (!selectedShow) return [];
    const show = shows.find(s => s.show_date_time === selectedShow);
    return show ? getFilteredComics(show) : [];
  };

  const getBookingForComic = (showDateTime: string, comicEmail: string): Booking | undefined => {
    return bookings.find(
      b => b.show_date_time === showDateTime && b.comic_email.toLowerCase() === comicEmail.toLowerCase()
    );
  };

  const handleBookComic = async (comic: AvailComic, show: Show, isHost: boolean) => {
    const bookingKey = `${show.show_date_time}-${comic.comic_email}`;
    setBookingInProgress(bookingKey);
    setError(null);
    setSuccessMessage(null);
    setWarningMessage(null);

    try {
      const response = await createBooking({
        comic_email: comic.comic_email,
        comic_name: comic.comic_name,
        show_date_time: show.show_date_time,
        show_display: show.show_display,
        is_host: isHost,
        send_email: true,  // Always send confirmation email when booking from avails
      });

      // Check if email was sent
      const emailSent = response.email_sent;

      if (emailSent) {
        setSuccessMessage(`Successfully booked ${comic.comic_name} ${isHost ? 'as host' : 'for spot'} on ${show.show_display} and sent confirmation email`);
        setTimeout(() => setSuccessMessage(null), 8000);
      } else {
        setWarningMessage(`Booked ${comic.comic_name} ${isHost ? 'as host' : 'for spot'} on ${show.show_display} but FAILED to send confirmation email. Please send manually.`);
        setTimeout(() => setWarningMessage(null), 10000);
      }

      // Reload bookings for this show to update the booked status
      await loadBookingsForShow(show.show_date_time);
    } catch (err: any) {
      console.error('Error booking comic:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to book comic';
      setError(errorMsg);
    } finally {
      setBookingInProgress(null);
    }
  };

  return (
    <div className="avails-list">
      <div className="avails-header">
        <h2>Comics Availability</h2>
        <button onClick={loadAvails} className="refresh-button" disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="close-success">×</button>
        </div>
      )}

      {warningMessage && (
        <div className="warning-message">
          {warningMessage}
          <button onClick={() => setWarningMessage(null)} className="close-warning">×</button>
        </div>
      )}

      <div className="filters-panel">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="start-date">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="gender-filter">Gender</label>
            <select
              id="gender-filter"
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="host-filter">Can Host</label>
            <select
              id="host-filter"
              value={hostFilter}
              onChange={(e) => setHostFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="friend-filter">Friend</label>
            <select
              id="friend-filter"
              value={friendFilter}
              onChange={(e) => setFriendFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="cellar-filter">Cellar Comic</label>
            <select
              id="cellar-filter"
              value={cellarFilter}
              onChange={(e) => setCellarFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="apply-button">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="clear-button">
            Clear Filters
          </button>
        </div>
      </div>

      <div className="summary-stats">
        <div className="stat">
          <span className="stat-label">Total Shows:</span>
          <span className="stat-value">{shows.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Avails:</span>
          <span className="stat-value">{getTotalComics()}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Can Host:</span>
          <span className="stat-value">{getTotalHosts()}</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading avails...</div>
      ) : shows.length === 0 ? (
        <div className="no-data">No availabilities found. Import avails data to get started.</div>
      ) : (
        <div className="avails-table-layout">
          <div className="show-selector">
            <h3>Select Show</h3>
            <div className="show-list">
              {shows.map((show) => (
                <div
                  key={show.show_date_time}
                  className={`show-item ${selectedShow === show.show_date_time ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedShow(show.show_date_time);
                    setSearchTerm('');
                    loadBookingsForShow(show.show_date_time);
                  }}
                >
                  <div className="show-name">{show.show_display}</div>
                  <div className="show-counts">
                    <span>{show.total_comics} comics</span>
                    <span>{show.hosts_count} hosts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="avails-table-section">
            {selectedShow ? (
              <>
                <div className="table-header">
                  <h3>{shows.find(s => s.show_date_time === selectedShow)?.show_display}</h3>
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="table-container">
                  <table className="avails-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Gender</th>
                        <th>Friend</th>
                        <th>Cellar</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSelectedShowComics().length === 0 ? (
                        <tr>
                          <td colSpan={5} className="no-results">
                            {searchTerm ? 'No comics match your search' : 'No comics available for this show'}
                          </td>
                        </tr>
                      ) : (
                        getSelectedShowComics().map((comic) => {
                          const show = shows.find(s => s.show_date_time === selectedShow)!;
                          const bookingKey = `${show.show_date_time}-${comic.comic_email}`;
                          const isBooking = bookingInProgress === bookingKey;
                          const existingBooking = getBookingForComic(show.show_date_time, comic.comic_email);

                          return (
                            <tr key={comic.comic_email}>
                              <td className="name-cell">{comic.comic_name}</td>
                              <td>
                                <span className={`gender-badge ${comic.gender}`}>
                                  {comic.gender === 'female' ? 'F' : 'M'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${comic.is_friend ? 'yes' : 'no'}`}>
                                  {comic.is_friend ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${comic.is_cellar_comic ? 'yes' : 'no'}`}>
                                  {comic.is_cellar_comic ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="actions-cell">
                                {existingBooking ? (
                                  <span className={`booked-badge ${existingBooking.is_host ? 'booked-host' : 'booked-spot'}`}>
                                    ✓ Booked{existingBooking.is_host ? ' (Host)' : ''}
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleBookComic(comic, show, false)}
                                      disabled={isBooking}
                                      className="book-button book-spot"
                                      title="Book for spot"
                                    >
                                      {isBooking ? '...' : 'Book Spot'}
                                    </button>
                                    {comic.can_host && (
                                      <button
                                        onClick={() => handleBookComic(comic, show, true)}
                                        disabled={isBooking}
                                        className="book-button book-host"
                                        title="Book as host"
                                      >
                                        {isBooking ? '...' : 'Book Host'}
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="no-show-selected">
                <p>← Select a show from the left to view available comics</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailsList;
